import React from 'react';
import Loader from './Loader';

const PROPERTY_OPTIONS = [
  { value: 'assignedDate', label: 'Assigned Date' },
  { value: 'deliveryMethod', label: 'Delivery Method' },
  { value: 'deliveryStatus', label: 'Delivery Status' },
  { value: 'notes', label: 'Notes' },
  { value: 'courierService', label: 'Courier Service' },
  { value: 'trackingId', label: 'Tracking ID' },
  { value: 'pickupPersonName', label: 'Pickup Person Name' },
  { value: 'pickupPersonMobile', label: 'Pickup Person Mobile' },
  { value: 'serialNumber', label: 'Serial Number' }
];

const DELIVERY_METHOD_PROPERTY_RULES = {
  0: ['assignedDate', 'deliveryMethod', 'notes', 'serialNumber'],
  1: ['assignedDate', 'deliveryMethod', 'deliveryStatus', 'notes', 'courierService', 'trackingId', 'serialNumber'],
  2: ['assignedDate', 'deliveryMethod', 'deliveryStatus', 'notes', 'pickupPersonName', 'pickupPersonMobile', 'serialNumber'],
  3: ['assignedDate', 'notes', 'serialNumber']
};

const DELIVERY_METHOD_LABELS = {
  0: 'Self Pickup',
  1: 'Courier',
  2: 'Other Person',
  3: 'Transfer'
};

const rowSurfaceStyle = {
  backgroundColor: 'rgba(17, 24, 39, 0.55)',
  borderColor: 'rgba(255, 193, 7, 0.22)'
};

function AssignmentBatchEditModal({
  show,
  saving,
  batchEditData,
  setBatchEditData,
  onClose,
  onSubmit
}) {
  const isBulkMode = batchEditData.selectedAssignments.length > 1;
  const selectedDeliveryMethods = [...new Set(
    (batchEditData.selectedAssignments || [])
      .map((assignment) => Number(assignment?.deliveryMethod))
      .filter((method) => !Number.isNaN(method))
  )];

  const allowedProperties = PROPERTY_OPTIONS.filter((option) => (
    selectedDeliveryMethods.length === 0
      ? true
      : selectedDeliveryMethods.every((method) => (DELIVERY_METHOD_PROPERTY_RULES[method] || ['notes']).includes(option.value))
  ));

  const allowedPropertyKeys = new Set(allowedProperties.map((option) => option.value));

  const updateSelectedProperty = (index, nextPropertyKey) => {
    if (!allowedPropertyKeys.has(nextPropertyKey)) {
      return;
    }

    const updatedProperties = [...batchEditData.selectedProperties];
    updatedProperties[index] = nextPropertyKey;

    setBatchEditData({
      ...batchEditData,
      selectedProperties: updatedProperties
    });
  };

  const getAvailableOptions = (currentPropertyKey) => PROPERTY_OPTIONS.filter((option) => (
    allowedPropertyKeys.has(option.value)
    && (option.value === currentPropertyKey || !batchEditData.selectedProperties.includes(option.value))
  ));

  const renderPropertyEditor = (propertyKey, compact = false) => {
    const wrapperProps = compact ? { className: 'm-0' } : {};
    const labelClassName = compact ? 'form-label small text-uppercase text-muted mb-2' : 'form-label';

    switch (propertyKey) {
      case 'assignedDate':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Assigned Date</label>
            <input
              type="date"
              className="form-control"
              value={batchEditData.assignedDate}
              onChange={(e) => setBatchEditData({ ...batchEditData, assignedDate: e.target.value })}
              required
            />
          </div>
        );
      case 'deliveryMethod':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Delivery Method</label>
            <select
              className="form-select"
              value={batchEditData.deliveryMethod}
              onChange={(e) => setBatchEditData({ ...batchEditData, deliveryMethod: parseInt(e.target.value, 10) })}
              required
            >
              <option value={0}>Self Pickup</option>
              <option value={1}>Courier</option>
              <option value={2}>Other Person</option>
            </select>
          </div>
        );
      case 'deliveryStatus':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Delivery Status</label>
            <select
              className="form-select"
              value={batchEditData.deliveryStatus}
              onChange={(e) => setBatchEditData({ ...batchEditData, deliveryStatus: parseInt(e.target.value, 10) })}
              required
            >
              <option value={0}>Pending</option>
              <option value={1}>In Transit</option>
              <option value={2}>Delivered</option>
            </select>
          </div>
        );
      case 'notes':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Notes</label>
            <textarea
              className="form-control"
              rows="3"
              placeholder="Add notes"
              value={batchEditData.notes}
              onChange={(e) => setBatchEditData({ ...batchEditData, notes: e.target.value })}
            ></textarea>
          </div>
        );
      case 'courierService':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Courier Service</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Blue Dart"
              value={batchEditData.courierService}
              onChange={(e) => setBatchEditData({ ...batchEditData, courierService: e.target.value })}
              required
            />
          </div>
        );
      case 'trackingId':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Tracking ID</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter tracking ID"
              value={batchEditData.trackingId}
              onChange={(e) => setBatchEditData({ ...batchEditData, trackingId: e.target.value })}
            />
          </div>
        );
      case 'pickupPersonName':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Pickup Person Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter name"
              value={batchEditData.pickupPersonName}
              onChange={(e) => setBatchEditData({ ...batchEditData, pickupPersonName: e.target.value })}
              required
            />
          </div>
        );
      case 'pickupPersonMobile':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Pickup Person Mobile</label>
            <input
              type="tel"
              className="form-control"
              placeholder="Enter mobile number"
              value={batchEditData.pickupPersonMobile}
              onChange={(e) => setBatchEditData({ ...batchEditData, pickupPersonMobile: e.target.value })}
              required
            />
          </div>
        );
      case 'serialNumber':
        return (
          <div key={propertyKey} {...wrapperProps}>
            <label className={labelClassName}>Serial Numbers</label>
            <div className="vstack gap-2">
              {batchEditData.selectedAssignments.map((assignment, index) => (
                <div key={assignment.id} className="border rounded p-3">
                  <div className="fw-semibold">{assignment.assetName}</div>
                  <div className="text-muted small mb-2">{assignment.employeeName}</div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter serial number"
                    value={assignment.serialNumber || ''}
                    onChange={(e) => {
                      const updatedAssignments = [...batchEditData.selectedAssignments];
                      updatedAssignments[index] = { ...updatedAssignments[index], serialNumber: e.target.value };
                      setBatchEditData({ ...batchEditData, selectedAssignments: updatedAssignments });
                    }}
                    required
                  />
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const addSelectedProperty = () => {
    if (!batchEditData.propertyToAdd
      || !allowedPropertyKeys.has(batchEditData.propertyToAdd)
      || batchEditData.selectedProperties.includes(batchEditData.propertyToAdd)) {
      return;
    }

    setBatchEditData({
      ...batchEditData,
      selectedProperties: [...batchEditData.selectedProperties, batchEditData.propertyToAdd],
      propertyToAdd: ''
    });
  };

  const removeSelectedProperty = (propertyKey) => {
    setBatchEditData({
      ...batchEditData,
      selectedProperties: batchEditData.selectedProperties.filter((property) => property !== propertyKey)
    });
  };

  return (
    <div className={`modal fade ${show ? 'show d-block' : ''}`} style={{ backgroundColor: show ? 'rgba(0,0,0,0.5)' : 'transparent' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-warning text-dark">
            <h5 className="modal-title">
              <i className="fas fa-layer-group me-2"></i>
              {isBulkMode ? 'Batch Edit Assignments' : 'Edit Assignment Details'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={onSubmit}>
            <div className="modal-body p-4">
              {isBulkMode && (
                <div className="alert alert-warning mb-4">
                  {batchEditData.selectedAssignments.length} selected assignment(s) will be updated together.
                </div>
              )}

              {isBulkMode ? (
                <div className="vstack gap-4">
                  <div>
                    <label className="form-label">Add Property</label>
                    {selectedDeliveryMethods.length > 0 && (
                      <div className="text-muted small mb-2">
                        Available fields depend on the common delivery workflow of the selected assignments: {selectedDeliveryMethods.map((method) => DELIVERY_METHOD_LABELS[method] || method).join(', ')}.
                      </div>
                    )}
                    <div className="border rounded-3 p-3" style={rowSurfaceStyle}>
                      <div className="row g-3 align-items-start">
                        <div className="col-lg-4">
                          <label className="form-label small text-uppercase text-muted mb-2">Property</label>
                          <select
                            className="form-select"
                            data-testid="bulk-property-select"
                            value={batchEditData.propertyToAdd}
                            onChange={(e) => setBatchEditData({ ...batchEditData, propertyToAdd: e.target.value })}
                          >
                            <option value="">Select property to update</option>
                            {allowedProperties
                              .filter((option) => !batchEditData.selectedProperties.includes(option.value))
                              .map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-lg-6">
                          {batchEditData.propertyToAdd ? (
                            renderPropertyEditor(batchEditData.propertyToAdd, true)
                          ) : (
                            <div className="text-muted small pt-4">
                              Select a property and enter the new value here.
                            </div>
                          )}
                        </div>
                        <div className="col-lg-2 d-flex align-items-start justify-content-lg-end pt-lg-4">
                          <button
                            type="button"
                            className="btn btn-warning w-100"
                            onClick={addSelectedProperty}
                            disabled={!batchEditData.propertyToAdd}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {batchEditData.selectedProperties.length > 0 ? (
                    <div className="vstack gap-3">
                      {batchEditData.selectedProperties.map((propertyKey, index) => (
                        <div key={`${propertyKey}-${index}`} className="border rounded-3 p-3" style={rowSurfaceStyle}>
                          <div className="row g-3 align-items-start">
                            <div className="col-lg-4">
                              <label className="form-label small text-uppercase text-muted mb-2">Property</label>
                              <select
                                className="form-select"
                                value={propertyKey}
                                onChange={(e) => updateSelectedProperty(index, e.target.value)}
                              >
                                {getAvailableOptions(propertyKey).map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-lg-6">
                              {renderPropertyEditor(propertyKey, true)}
                            </div>
                            <div className="col-lg-2 d-flex align-items-start justify-content-lg-end pt-lg-4">
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => removeSelectedProperty(propertyKey)}
                                aria-label={`Remove ${PROPERTY_OPTIONS.find((option) => option.value === propertyKey)?.label || propertyKey}`}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted small">
                      Add one property at a time. Each added row will be included in the bulk update.
                    </div>
                  )}
                </div>
              ) : (
                <div className="vstack gap-4">
                  <div className="border rounded p-3">
                    <div className="fw-semibold">{batchEditData.selectedAssignments[0]?.assetName || 'Unknown Asset'}</div>
                    <div className="text-muted small">{batchEditData.selectedAssignments[0]?.employeeName || 'Unknown'}</div>
                    <label className="form-label mt-3">Serial Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter serial number"
                      value={batchEditData.selectedAssignments[0]?.serialNumber || ''}
                      onChange={(e) => {
                        const updatedAssignments = [...batchEditData.selectedAssignments];
                        if (updatedAssignments[0]) {
                          updatedAssignments[0] = { ...updatedAssignments[0], serialNumber: e.target.value };
                          setBatchEditData({ ...batchEditData, selectedAssignments: updatedAssignments });
                        }
                      }}
                      required
                    />
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Assigned Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={batchEditData.assignedDate}
                        onChange={(e) => setBatchEditData({ ...batchEditData, assignedDate: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Delivery Method</label>
                      <select
                        className="form-select"
                        value={batchEditData.deliveryMethod}
                        onChange={(e) => {
                          const deliveryMethod = parseInt(e.target.value, 10);
                          setBatchEditData({
                            ...batchEditData,
                            deliveryMethod,
                            deliveryStatus: deliveryMethod === 0 ? 2 : batchEditData.deliveryStatus,
                            courierService: deliveryMethod === 1 ? batchEditData.courierService : '',
                            trackingId: deliveryMethod === 1 ? batchEditData.trackingId : '',
                            pickupPersonName: deliveryMethod === 2 ? batchEditData.pickupPersonName : '',
                            pickupPersonMobile: deliveryMethod === 2 ? batchEditData.pickupPersonMobile : ''
                          });
                        }}
                        required
                      >
                        <option value={0}>Self Pickup</option>
                        <option value={1}>Courier</option>
                        <option value={2}>Other Person</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Delivery Status</label>
                      <select
                        className="form-select"
                        value={batchEditData.deliveryStatus}
                        onChange={(e) => setBatchEditData({ ...batchEditData, deliveryStatus: parseInt(e.target.value, 10) })}
                      >
                        <option value={0}>Pending</option>
                        <option value={1}>In Transit</option>
                        <option value={2}>Delivered</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Add notes"
                        value={batchEditData.notes}
                        onChange={(e) => setBatchEditData({ ...batchEditData, notes: e.target.value })}
                      ></textarea>
                    </div>
                    {batchEditData.deliveryMethod === 1 && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label">Courier Service</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Blue Dart"
                            value={batchEditData.courierService}
                            onChange={(e) => setBatchEditData({ ...batchEditData, courierService: e.target.value })}
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Tracking ID</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter tracking ID"
                            value={batchEditData.trackingId}
                            onChange={(e) => setBatchEditData({ ...batchEditData, trackingId: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                    {batchEditData.deliveryMethod === 2 && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label">Pickup Person Name</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter name"
                            value={batchEditData.pickupPersonName}
                            onChange={(e) => setBatchEditData({ ...batchEditData, pickupPersonName: e.target.value })}
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Pickup Person Mobile</label>
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Enter mobile number"
                            value={batchEditData.pickupPersonMobile}
                            onChange={(e) => setBatchEditData({ ...batchEditData, pickupPersonMobile: e.target.value })}
                            required
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer bg-light border-0 p-4">
              <button type="button" className="btn btn-outline-secondary btn-lg" onClick={onClose}>
                <i className="fas fa-times me-2"></i>Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-warning btn-lg ${saving ? 'btn-loading' : ''}`}
                disabled={
                  saving ||
                  batchEditData.selectedAssignments.length === 0 ||
                  (isBulkMode && batchEditData.selectedProperties.length === 0) ||
                  (isBulkMode && batchEditData.selectedProperties.includes('serialNumber') && batchEditData.selectedAssignments.some((assignment) => !assignment.serialNumber.trim())) ||
                  (!isBulkMode && !batchEditData.selectedAssignments[0]?.serialNumber?.trim())
                }
              >
                {saving ? (
                  <>
                    <Loader size="small" />
                    <span className="ms-2">Processing...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-layer-group me-2"></i>
                    {isBulkMode ? 'Update Selected' : 'Update Assignment'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AssignmentBatchEditModal;
